import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose } from 'react-icons/md';

interface RepeatCustomisationModalProps {
  isOpen: boolean;
  itemName: string;
  modifiers?: any[];
  onRepeat: () => void;
  onChoose: () => void;
  onClose: () => void;
}

export const RepeatCustomisationModal: React.FC<RepeatCustomisationModalProps> = ({
  isOpen,
  itemName,
  modifiers,
  onRepeat,
  onChoose,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100000] p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg max-h-[90dvh] flex flex-col overflow-hidden relative"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 sm:p-2.5 hover:bg-gray-100 rounded-full transition-colors z-10"
              aria-label="Close"
            >
              <MdClose className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </button>

            {/* Header Section */}
            <div className="bg-gradient-to-r from-gray-200 to-gray-300 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1 pr-8">
                {itemName}
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 pr-8">
                Repeat previous customisation?
              </p>
            </div>

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
              {/* Modifiers - NEW Format */}
              {modifiers && modifiers.length > 0 ? (
                modifiers.map((mod: any, idx: number) => (
                  <div key={idx}>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-2">{mod.name}:</p>
                    <div className="bg-gray-100 border border-gray-200 rounded-lg p-2.5 sm:p-3">
                      <p className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                        {mod.options.map((opt: any) => opt.name).join(', ')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <p className="text-sm sm:text-base text-gray-600 text-center">
                    No customizations
                  </p>
                </div>
              )}
            </div>

            {/* Sticky Footer - Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 sm:p-6 pt-3 sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
              <div className="flex gap-3">
                <button
                  onClick={onChoose}
                  className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 text-sm sm:text-base font-semibold rounded-xl border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all min-h-[48px]"
                >
                  I'll choose
                </button>
                <button
                  onClick={onRepeat}
                  className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 text-sm sm:text-base font-semibold rounded-xl text-white transition-all hover:opacity-90 min-h-[48px]"
                  style={{ backgroundColor: '#EF4444' }}
                >
                  Repeat
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RepeatCustomisationModal;
